import { styled } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import type { FC } from 'react';
import React, { memo, useMemo, useRef } from 'react';

import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { expVal } from '@confluence/feature-experiments';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { extensionToADF } from '@confluence/fabric-extension-lib/entry-points/editor-extensions.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import {
	FlatStyleTocItemContainer,
	ListStyleTocItemContainer,
	ListStyleTocLevelContainer,
} from './TableOfContentsElementStructure.tsx';
import { renderReducedToc } from './renderTocDirect.tsx';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { EmptyTocItem } from './renderTocDirect.tsx';
import { handleTocNode } from './handleADF.ts';
// eslint-disable-next-line import/no-cycle
import { generateTocTreeStructure } from './generateTocTreeStructure.tsx';
// eslint-disable-next-line import/no-cycle
import { TableOfContentsComponent } from './TableOfContentsComponent.tsx';
import { useCurrentAdf } from './useCurrentAdf.ts';

export const nonPrintableWrapperTestId = 'non-printable-wrapper';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const NonPrintableWrapper = styled.div({
	overflow: 'auto',
	'@media print': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'ul ul:not(:first-of-type), ol ul:not(:first-of-type)': {
		marginTop: '0px',
	},
});

export const printableWrapperTestId = 'printable-wrapper';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const PrintableWrapper = styled.div({
	overflow: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'ul ul:not(:first-of-type), ol ul:not(:first-of-type)': {
		marginTop: '0px',
	},
});

export interface ElementFactoryInterface {
	createTocLevelContainer: Function;
	createTocItemContainer: Function;
	decorateToc?: Function;
}

export interface SsrElementContentInterface {
	text: string;
	type: string;
	innerHTML?: string;
}

export interface SsrElementInterface {
	type: string;
	id?: string;
	attrs: {
		level: number;
		startPos?: string;
	};
	content: SsrElementContentInterface[];
}

export interface TableOfContentsInterface {
	adf: ADNode | string | null;
	cloudId: string;
	userId: string;
	macroDefaultProps: ExtensionHandlerProps;
	isInEditMode?: boolean;
}

export function getSeparators(separator: string): string[] {
	switch (separator.toLowerCase()) {
		case 'braces':
			return ['{ ', ' } { ', ' }'];
		case 'pipe':
			return ['', ' | ', ''];
		case 'parens':
			return ['( ', ' ) ( ', ' )'];
		case '':
		case 'brackets':
			// brackets in the ADF is an empty string
			return ['[ ', ' ] [ ', ' ]'];
		default:
			// Match separator usage for custom separators
			return ['', separator, ''];
	}
}

/**
 * Builder for the `attributes` payload sent on the TOC `viewMacroExperience` success event.
 * The `confluence-feature-gating/inline-usage` lint rule forbids `expVal(...)` as a property value inside a JSX-bound `ObjectExpression`
 */
const buildTocSuccessAttributes = (
	perfOptimizationsEnabled: boolean,
	handleTocDurationMs: number | null,
	treeBuildDurationMs: number | null,
	reducedDomDurationMs: number | null,
	headingCount: number,
	structure: string,
	usedReducedDomPath: boolean,
) => ({
	tocHandleAdfDurationMs: handleTocDurationMs === null ? null : Math.round(handleTocDurationMs),
	tocTreeBuildDurationMs: treeBuildDurationMs === null ? null : Math.round(treeBuildDurationMs),
	tocReducedDomDurationMs: reducedDomDurationMs === null ? null : Math.round(reducedDomDurationMs),
	tocHeadingCount: headingCount,
	tocStructure: structure,
	tocUsedReducedDomPath: usedReducedDomPath,
	tocPerfOptimizationsEnabled: perfOptimizationsEnabled,
});

const TableOfContentsContainer: FC<TableOfContentsInterface> = ({
	cloudId,
	userId,
	adf,
	macroDefaultProps,
	isInEditMode = false,
}) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { node, mode, contentId, extensionKey } = macroDefaultProps;

	const currentAdf = useCurrentAdf(adf, isInEditMode);
	const macroNode = useMemo(() => extensionToADF(node), [node]);

	// Phase-timing for the perf-optimization A/B. We measure inside each `useMemo`
	// so the duration only reflects real work (not cache hits).
	const handleTocDurationRef = useRef<number | null>(null);
	const treeBuildDurationRef = useRef<number | null>(null);
	const reducedDomDurationRef = useRef<number | null>(null);

	handleTocDurationRef.current = null;
	const tocOptions = useMemo(() => {
		const start = performance.now();
		try {
			return handleTocNode(macroNode, currentAdf);
		} finally {
			handleTocDurationRef.current = performance.now() - start;
		}
	}, [macroNode, currentAdf]);
	const name = getExperienceName(mode, node);

	const ElementFactory = useMemo(
		() => (tocOptions.structure === 'flat' ? flatTocElementFactory() : listTocElementFactory()),
		[tocOptions.structure],
	);

	const separators = useMemo(
		() => getSeparators(tocOptions.separators || ''),
		[tocOptions.separators],
	);

	const initialSeparatorRef = { current: true };

	const [preseparator, midseparator, postseparator] = separators;
	const flatSeparators = useMemo(
		() =>
			tocOptions.structure === 'flat'
				? {
						'data-preseparator': preseparator,
						'data-midseparator': midseparator,
						'data-postseparator': postseparator,
					}
				: {},
		[tocOptions.structure, preseparator, midseparator, postseparator],
	);

	treeBuildDurationRef.current = null;
	const { elementsArray, error } = useMemo(() => {
		const start = performance.now();
		try {
			return {
				elementsArray: generateTocTreeStructure(tocOptions.headerelements, [], cloudId, userId),
				error: null,
			};
		} catch (error) {
			return {
				elementsArray: null,
				error,
			};
		} finally {
			treeBuildDurationRef.current = performance.now() - start;
		}
	}, [tocOptions?.headerelements, cloudId, userId]);

	// Memoize the stringified macro params used as a DOM data attribute so we
	// don't pay the JSON.stringify cost on every render
	const macroParamsAttr = useMemo(
		() => JSON.stringify(macroNode?.attrs?.parameters?.macroParams),
		[macroNode?.attrs?.parameters?.macroParams],
	);

	reducedDomDurationRef.current = null;
	const reducedDomContent = useMemo(() => {
		if (!expVal('confluence_toc_reduce_dom_nodes', 'isEnabled', false)) return null;
		const start = performance.now();
		try {
			return renderReducedToc(tocOptions, separators, cloudId, userId);
		} finally {
			reducedDomDurationRef.current = performance.now() - start;
		}
	}, [tocOptions, separators, cloudId, userId]);

	if (error) {
		const attributes = getMacroAttributesFromADFNode(node);

		return (
			<MacroExperienceFailure
				name={name}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				error={error || new Error(`${extensionKey} failed to render`)}
				attributes={attributes}
				source="TableOfContents"
			/>
		);
	}

	// Wrap tocContent for printable:
	let Wrapper: typeof NonPrintableWrapper | typeof PrintableWrapper;
	let wrapperTestId: typeof nonPrintableWrapperTestId | typeof printableWrapperTestId;
	let wrapperVc: string;
	if (tocOptions.printable === 'false') {
		Wrapper = NonPrintableWrapper;
		wrapperTestId = nonPrintableWrapperTestId;
		wrapperVc = 'non-printable-toc-wrapper';
	} else {
		Wrapper = PrintableWrapper;
		wrapperTestId = printableWrapperTestId;
		wrapperVc = 'printable-toc-wrapper';
	}

	return (
		<Wrapper
			title="Macro (toc)"
			data-fabric-macro={tocOptions.macroId}
			data-macro-body
			data-macro-parameters={macroParamsAttr}
			data-testid={wrapperTestId}
			data-vc={wrapperVc}
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': tocOptions.macroId }
				: { 'data-ssr-placeholder-replace': tocOptions.macroId })}
		>
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={tocOptions.className}
				data-numberedoutline={tocOptions.outline}
				data-cssliststyle={tocOptions.cssliststyle}
				data-csslistindent={tocOptions.csslistindent}
				data-headerelements={tocOptions.headerRange}
				data-hasbody={tocOptions.hasbody}
				data-macro-name={tocOptions.name}
				data-macro-id={tocOptions.macroId}
				data-structure={tocOptions.structure}
				data-vc="toc"
				{...flatSeparators}
				{...(renderServerPlaceholder
					? { 'data-ssr-placeholder': `toc-${tocOptions.macroId}` }
					: { 'data-ssr-placeholder-replace': `toc-${tocOptions.macroId}` })}
			>
				{reducedDomContent ?? (
					<TableOfContentsComponent
						elementsArray={elementsArray}
						elementFactory={ElementFactory}
						config={tocOptions}
						separators={separators}
						initialSeparatorRef={initialSeparatorRef}
						isInEditMode={isInEditMode}
					/>
				)}
				<MacroExperienceSuccess
					name={name}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={buildTocSuccessAttributes(
						expVal('confluence_toc_render_perf', 'isEnabled', false),
						handleTocDurationRef.current,
						treeBuildDurationRef.current,
						reducedDomDurationRef.current,
						Array.isArray(tocOptions?.headerelements) ? tocOptions.headerelements.length : 0,
						tocOptions?.structure ?? 'unknown',
						reducedDomContent !== null,
					)}
				/>
			</div>
		</Wrapper>
	);
};

/**
 * Creates HTML elements for use in building a flat-style TOC
 */
function flatTocElementFactory() {
	return {
		createTocLevelContainer() {
			return FlatStyleTocItemContainer;
		},
		createTocItemContainer() {
			return FlatStyleTocItemContainer;
		},
		decorateToc() {
			return true;
		},
	};
}

/**
 * Creates HTML elements for use in building a list-style TOC
 */
function listTocElementFactory() {
	return {
		createTocLevelContainer() {
			return ListStyleTocLevelContainer;
		},
		createTocItemContainer() {
			return ListStyleTocItemContainer;
		},
	};
}

const TableOfContentsWithBoundary = withErrorBoundary({
	attribution: Attribution.BACKBONE,
})(TableOfContentsContainer);

// Single `memo()` with a custom comparator that branches on the experiment value at compare-time.
const propsAreEqual = (
	prev: Readonly<TableOfContentsInterface>,
	next: Readonly<TableOfContentsInterface>,
) => {
	if (expVal('confluence_toc_render_perf', 'isEnabled', false)) {
		// Default shallow compare — same semantics as `memo(component)` with no comparator.
		const prevKeys = Object.keys(prev) as (keyof TableOfContentsInterface)[];
		const nextKeys = Object.keys(next) as (keyof TableOfContentsInterface)[];
		if (prevKeys.length !== nextKeys.length) return false;
		for (const key of prevKeys) {
			if (prev[key] !== next[key]) return false;
		}
		return true;
	}
	return isEqual(prev, next);
};

export const TableOfContents: React.NamedExoticComponent<TableOfContentsInterface> = memo(
	TableOfContentsWithBoundary,
	propsAreEqual,
);
